import { OrganizationReportType } from "@/admin/insights/__generated__/AdminReportsDashboardQuery.graphql"
import { OrganizationKind } from "@/core/context/__generated__/ActiveOrganizationContextFragment.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"

/**
 * Returns a custom value scoped to the currently active organization kind,
 * such as "community" or "workspace". This hook uses a static configuration
 * to provide consistent values that may vary depending on organization context.
 *
 * These values are not feature flags, but rather contextual settings that help
 * simplify behavior, naming, or logic based on the organization type. For example,
 * the report type label may differ between org kinds.
 *
 * This helps create a more streamlined and domain-appropriate experience for
 * different organization models, without relying on complex conditional logic.
 *
 * NOTE: This is separate from entitlements, paywalls, or feature flags, which
 * are managed elsewhere in the platform and can differ for each individual org.
 */

// eslint-disable-next-line @typescript-eslint/ban-types
type ScopedValuesConfigContext = {}

type ScopedValuesConfigOpts = {
  context: ScopedValuesConfigContext
}

type ScopedValuesConfig = {
  "insights-report-type": OrganizationReportType
  "product-team-settings-label": "Team" | "Roles"
  "product-team-settings-invite-button-label": string
}

type GetScopedValuesConfig = (opts: ScopedValuesConfigOpts) => ScopedValuesConfig

const COMMUNITY_SCOPED_VALUES: GetScopedValuesConfig = () => ({
  "insights-report-type": "globalInsights",
  "product-team-settings-label": "Team",
  "product-team-settings-invite-button-label": "Add to Team",
})

const WORKSPACE_SCOPED_VALUES: GetScopedValuesConfig = () => ({
  "insights-report-type": "workspaceInsights",
  "product-team-settings-label": "Roles",
  "product-team-settings-invite-button-label": "Assign Role",
})

export type ScopedValueType = keyof ScopedValuesConfig

const ORGANIZATION_SCOPED_VALUES: Record<OrganizationKind, GetScopedValuesConfig> = {
  community: COMMUNITY_SCOPED_VALUES,
  workspace: WORKSPACE_SCOPED_VALUES,
  "%future added value": COMMUNITY_SCOPED_VALUES,
}

export function useScopedValues() {
  const activeOrganization = useActiveOrganization()

  if (!activeOrganization) {
    throw new Error("useScopedValues must be used within ActiveOrganizationContext")
  }

  const context: ScopedValuesConfigContext = {}

  const scopedValues = ORGANIZATION_SCOPED_VALUES[activeOrganization.kind]({ context })

  function getScopedValue<K extends keyof ScopedValuesConfig>(
    key: K
  ): ScopedValuesConfig[K] {
    return scopedValues[key]
  }

  return getScopedValue
}
